import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { UploadReceipt } from "../api/types";
import { ProgressBar, Spinner } from "../components/ui";

const KEY = "argus.pending-upload.v1";
const roles = ["left", "right", "gaze"] as const;
type Role = typeof roles[number];
const size = (n: number) => n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : `${(n / 1024 ** 2).toFixed(1)} MB`;
const fingerprint = (f: File) => [f.name, f.size, f.lastModified].join(":");

export function UploadDialog({ onClose, onImported, onOpen }: { onClose: () => void; onImported: () => void; onOpen: (id: string) => void }) {
  const [name, setName] = useState("");
  const [files, setFiles] = useState<Partial<Record<Role, File>>>({});
  const [receipt, setReceipt] = useState<UploadReceipt | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(0);
  const abort = useRef<AbortController | null>(null);
  const done = useRef(false);
  const [restoring, setRestoring] = useState(true);
  const currentJob = receipt?.processing_job ?? receipt?.job;

  useEffect(() => {
    let alive = true;
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || "null");
      if (saved) api.uploadStatus(saved.id).then(r => { if (alive) { setReceipt(r); setName(r.name); setSent(r.files.reduce((n, f) => n + f.received_bytes, 0)); } })
        .catch(() => localStorage.removeItem(KEY)).finally(() => { if (alive) setRestoring(false); });
      else setRestoring(false);
    } catch { localStorage.removeItem(KEY); setRestoring(false); }
    return () => { alive = false; abort.current?.abort(); };
  }, []);

  useEffect(() => {
    if (!receipt || !currentJob || !["queued", "running"].includes(currentJob.state)) return;
    let alive = true;
    const timer = window.setInterval(() => api.uploadStatus(receipt.id).then(r => {
      if (!alive) return;
      setReceipt(r);
      if ((r.processing_job ?? r.job)?.state === "succeeded" && !done.current) { done.current = true; localStorage.removeItem(KEY); onImported(); }
    }).catch(e => { if (alive) setError(e.message); }), 1000);
    return () => { alive = false; clearInterval(timer); };
  }, [receipt?.id, currentJob?.state, onImported]);

  const total = receipt?.files.reduce((n, f) => n + f.size_bytes, 0) ?? Object.values(files).reduce((n, f) => n + (f?.size ?? 0), 0);
  const processing = !!currentJob && ["queued", "running"].includes(currentJob.state);
  const succeeded = currentJob?.state === "succeeded";
  const failed = currentJob && ["failed", "cancelled"].includes(currentJob.state);

  async function start() {
    setError(""); setBusy(true); abort.current = new AbortController();
    try {
      let row = receipt;
      if (row?.job) { row = await api.completeUpload(row.id); setReceipt(row); return; }
      if (!row) {
        if (!files.left || !files.right) throw new Error("Select both stereo videos.");
        row = await api.createUpload(name.trim() || files.left.name.replace(/\.[^.]+$/, ""), roles.filter(r => files[r]).map(r => ({role: r, name: files[r]!.name, size_bytes: files[r]!.size})));
        localStorage.setItem(KEY, JSON.stringify({id: row.id, fingerprints: Object.fromEntries(roles.filter(r => files[r]).map(r => [r, fingerprint(files[r]!)]))}));
        setReceipt(row);
      }
      const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
      for (const spec of row.files) {
        if (spec.received_bytes === spec.size_bytes) continue;
        const file = files[spec.role as Role];
        if (!file || fingerprint(file) !== saved.fingerprints?.[spec.role]) throw new Error(`Select the same ${spec.role} file to resume: ${spec.name}`);
      }
      for (const spec of row.files) {
        const file = files[spec.role as Role];
        while (spec.received_bytes < spec.size_bytes) {
          const from = spec.received_bytes;
          const res = await fetch(`/api/v1/uploads/${row.id}/files/${spec.role}?offset=${from}`, {
            method: "PUT", headers: {"Content-Type": "application/octet-stream"}, body: file!.slice(from, from + row.chunk_bytes), signal: abort.current.signal,
          });
          if (!res.ok) throw new Error((await res.json()).detail || `Upload failed (${res.status})`);
          spec.received_bytes = (await res.json()).received_bytes;
          setSent(row.files.reduce((n, f) => n + f.received_bytes, 0));
        }
      }
      row = await api.completeUpload(row.id); setReceipt(row); onImported();
    } catch (e) {
      setError(e instanceof DOMException && e.name === "AbortError" ? "Transfer paused. Select Resume to continue." : (e as Error).message);
      // A lost response may still have been written. Resume from the server's offset.
      const saved = JSON.parse(localStorage.getItem(KEY) || "null");
      if (saved) try { const r = await api.uploadStatus(saved.id); setReceipt(r); setSent(r.files.reduce((n, f) => n + f.received_bytes, 0)); } catch { /* retain last known state */ }
    } finally { setBusy(false); }
  }

  async function discard() {
    try {
      if (receipt?.state === "uploading") await api.discardUpload(receipt.id);
      localStorage.removeItem(KEY); setReceipt(null); setFiles({}); setSent(0); setError(""); done.current = false;
    } catch (e) { setError((e as Error).message); }
  }

  const readyId = currentJob?.result?.session_id ?? receipt?.session_id;
  return <div className="modal-backdrop"><section className="upload-dialog" role="dialog" aria-modal="true" aria-labelledby="upload-title">
    <header className="row"><div><div className="eyebrow">INGEST / STEREO + GAZE</div><h2 id="upload-title">Upload a capture</h2></div><span className="spacer" /><button className="ghost" aria-label="Close upload" onClick={onClose}>✕</button></header>
    <p className="upload-intro">Your files are stored on this Studio server. Indexing, object detection and annotation proposals run automatically.</p>
    {restoring ? <Spinner /> : <>
      <label htmlFor="capture-name">Capture name</label><input id="capture-name" value={name} disabled={!!receipt || busy} placeholder="e.g. Kitchen · morning routine" onChange={e => setName(e.target.value)} />
      {!processing && !succeeded && !failed && <div className="upload-slots">{roles.map(role => <label className={`upload-slot ${files[role] ? "chosen" : ""}`} key={role}>
        <span className="upload-slot-role">{role === "gaze" ? "◎" : role === "left" ? "L" : "R"}</span><strong>{role === "gaze" ? "Gaze stream" : `${role === "left" ? "Left" : "Right"} camera`}</strong>
        <span className="tiny muted">{files[role]?.name || receipt?.files.find(f => f.role === role)?.name || (role === "gaze" ? "Optional · normalized JSONL" : "MP4, MOV or MKV")}</span>
        <input aria-label={`${role} file`} type="file" accept={role === "gaze" ? ".jsonl" : ".mp4,.mov,.mkv"} disabled={busy} onChange={e => setFiles(f => ({...f, [role]: e.target.files?.[0]}))} />
      </label>)}</div>}
      <div className="upload-process"><span>01 / Upload</span><span>02 / Index + analyze</span><span>03 / Review</span></div>
      {busy || (receipt && !processing && !succeeded && !failed) ? <div className="col"><div className="row tiny"><span>{size(sent)} / {size(total)}</span><span className="spacer" /><span>{Math.round(sent / Math.max(1, total) * 100)}%</span></div><ProgressBar value={sent / Math.max(1, total)} state="running" /></div> : null}
      {processing && <div className="upload-status"><Spinner /><div><strong>Processing on the server</strong><p>{currentJob?.message || "Waiting for a worker…"}</p><span className="tiny muted">You can close this dialog. Progress is saved with the capture.</span></div></div>}
      {succeeded && <div className="upload-status complete"><span>✓</span><div><strong>{currentJob?.result?.pipeline?.state === "complete" ? "Capture ready for review" : "Capture saved · analysis needs attention"}</strong><p>Originals, stereo previews and available analysis are in your library.</p></div></div>}
      {(error || failed) && <div className="upload-error" role="alert">{error || currentJob?.error || "Processing was cancelled. Retry when ready."}</div>}
      <footer className="row"><span className="tiny muted">{total ? size(total) : "20 GiB default limit"} · resumable transfer</span><span className="spacer" />
        {receipt && !busy && !processing && <button className="ghost" onClick={discard}>{succeeded || failed ? "New upload" : "Discard transfer"}</button>}
        {busy ? <button onClick={() => abort.current?.abort()}>Pause transfer</button> : succeeded && readyId ? <button className="primary" onClick={() => { onImported(); onOpen(readyId); onClose(); }}>Open review →</button> : !processing ? <button className="primary" disabled={!receipt && (!files.left || !files.right)} onClick={start}>{failed ? "Retry processing" : receipt ? "Resume upload" : "Upload & analyze"}</button> : <button onClick={onClose}>Back to library</button>}
      </footer>
    </>}
  </section></div>;
}
